<script lang="ts">
    import BaseTab from "@/tabs/mixins/BaseTab";

    export default {
        extends: BaseTab,
        name: "TypoperationForm",
        tabName: "Typoperation",
    };
</script>

<script setup lang="ts">
    import PrioritetAutocomplete from "@/components/forms/reusables/PrioritetAutocomplete.vue";
    import PatientlageAutocomplete from "@/components/forms/reusables/PatientlageAutocomplete.vue";
    import SidaAutocomplete from "@/components/forms/reusables/SidaAutocomplete.vue";
    import AnestesiAutocomplete from "@/components/forms/reusables/AnestesiAutocomplete.vue";
    import BlockadAutocomplete from "@/components/forms/reusables/BlockadAutocomplete.vue";
    import BioperationskodRepeater from "@/components/forms/reusables/BioperationskodRepeater.vue";
    import BidiagnoskodRepeater from "@/components/forms/reusables/BidiagnoskodRepeater.vue";
    import HuvudoperationskodAutocomplete from "@/components/forms/reusables/HuvudoperationskodAutocomplete.vue";
    import HuvuddiagnoskodAutocomplete from "@/components/forms/reusables/HuvuddiagnoskodAutocomplete.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import HjalpmedelRepeater from "@/components/forms/reusables/HjalpmedelRepeater.vue";
    import StudieKvalitetsregisterRepeater from "@/components/forms/reusables/StudieKvalitetsregisterRepeater.vue";
    import TrombosprofylaxAutocomplete from "@/components/forms/reusables/TrombosprofylaxAutocomplete.vue";
    import AntibiotikaprofylaxAutocomplete from "@/components/forms/reusables/AntibiotikaprofylaxAutocomplete.vue";
    import KontaktorsakAutocomplete from "@/components/forms/reusables/KontaktorsakAutocomplete.vue";
    import ResursRepeater from "@/components/forms/reusables/ResursRepeater.vue";
    import ImplantatRepeater from "@/components/forms/reusables/ImplantatRepeater.vue";
    import VardformAutocomplete from "@/components/forms/reusables/VardformAutocomplete.vue";
    import KNumber from "@/components/forms/inputs/Number/KNumber.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import { toRefs, ref, defineProps, defineExpose, computed } from "vue";
    import { useStore } from "@/store";
    import { convertPrioritetData } from "@/components/forms/reusables/prioritetHelper";
    import { convertDiagnoskodData, Diagnoskod } from "@/store/modules/diagnoskodModule";
    import { convertOperationskodData, Operationskod } from "@/store/modules/operation/operationskodModule";
    import { convertAnestesiData } from "@/store/modules/operation/anestesiModule";
    import { convertBlockadData } from "@/store/modules/operation/blockadModule";
    import { openDialog } from "@/utils";
    import cloneDeep from "lodash/cloneDeep";
    import { convertHjalpmedelData, Hjalpmedel } from "@/components/forms/reusables/hjalpmedelHelper";
    import {
        convertStudieKvalitetsregisterData,
        StudieKvalitetsregister,
    } from "@/components/forms/reusables/studieKvalitetsregisterHelper";
    import { convertResursData, Resurs } from "@/components/forms/reusables/resurserHelper";
    import { convertImplantatData } from "@/components/forms/reusables/implantatHelper";
    import { convertVardformData } from "@/store/modules/operation/vardformModule";
    import { getVardformFromValue } from "@/components/forms/reusables/vardformHelper";

    import emitter from "tiny-emitter/instance";

    const props = defineProps({
        uid: Number,
        tabId: Number,
        parameters: Object,
        editing: Boolean,
    });

    const { parameters, tabId } = toRefs(props);

    const store = useStore();

    const can = store.getters["ability/can"];

    const typoperation = parameters?.value?.typoperation ?? null;

    const formRef = ref();
    const formErrors = ref([]);
    const inputErrors = ref({});

    const hasUnsavedData = computed(() => {
        return formRef.value?.hasUnsavedData();
    });

    const heading = computed(() => {
        return typoperation?.id ? "Ändra på typoperation" : "Skapa ny typoperation";
    });

    defineExpose({
        hasUnsavedData,
    });

    const initForm = async () => {
        let formData: any = cloneDeep(typoperation);

        if (typoperation) {
            formData = {
                ...formData,
                prio: convertPrioritetData({ prioritet: formData.prio_display }),
                huvuddiagnos: convertDiagnoskodData({
                    diagnoskod: formData.huvuddiagnos_display,
                }),
                bidiagnoser: formData.bidiagnoser_display?.map((item: Diagnoskod) => {
                    return { id: convertDiagnoskodData({ diagnoskod: item }) };
                }),
                huvudoperationskod: convertOperationskodData({
                    operationskod: formData.huvudoperationskod_display,
                }),
                opkoder: formData.opkoder_display?.map((item: Operationskod) => {
                    return {
                        id: convertOperationskodData({ operationskod: item }),
                    };
                }),
                implantat: formData.implantat_display?.map((item: any) => {
                    return {
                        id: convertImplantatData({ implantat: item }),
                    };
                }),
                planerad_vardform: convertVardformData({
                    vardform: formData.planerad_vardform_display,
                }),
                resurser: formData.resurser_display?.map((item: Resurs) => {
                    return {
                        id: convertResursData({ resurs: item }),
                    };
                }),
                anestesi: convertAnestesiData({ anestesi: formData.anestesi_display }),
                blockad: convertBlockadData({ blockad: formData.blockad_display }),
                hjalpmedel: formData.hjalpmedel_display?.map((item: Hjalpmedel) => {
                    return {
                        id: convertHjalpmedelData({ hjalpmedel: item }),
                    };
                }),
                studie_kvalitetsregister: formData.studie_kvalitetsregister_display?.map(
                    (item: StudieKvalitetsregister) => ({
                        id: convertStudieKvalitetsregisterData({ studieKvalitetsregister: item }),
                    })
                ),
                operationsinformation: formData.operationsinformation ?? "",
            };
        }

        return formData;
    };

    const submitForm = async (data: any) => {
        try {
            await store.dispatch("operation/typoperationer/save", { ...data, id: typoperation?.id });
            store.dispatch("tabs/closeTab", tabId?.value);
        } catch (e: any) {
            if (e.response) {
                formErrors.value = e.response.data["non_field_errors"];
                inputErrors.value = e.response.data;
            }
            openDialog("Kunde inte spara typoperationen", "error");
        }
    };

    const cancelForm = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId?.value);
                        },
                    },
                ],
            });
        } else {
            store.dispatch("tabs/closeTab", tabId?.value);
        }
    };

    const formTriggers = [
        {
            fieldName: "blockad",
            dependentOn: "anestesi",
            action: ({
                value,
                dependentOnValue,
                selectedOption,
                initial = false,
            }: {
                value: any;
                dependentOnValue: any;
                selectedOption: any;
                initial?: boolean;
            }) => {
                /* dependentOnValue kan vara ett objekt vid inladdning av data i formuläret men är senare id på
                anestesi, och då tar vi första alternativet i selectedOption. */
                const isBlockad =
                    dependentOnValue?.is_blockad || (selectedOption?.length > 0 && selectedOption[0]?.is_blockad);
                return {
                    value: isBlockad || initial ? value : null,
                    disabled: !isBlockad,
                };
            },
        },
        {
            fieldName: "antal_dagar_pa_avdelning",
            dependentOn: "planerad_vardform",
            action: ({ value, dependentOnValue }: { value: any; dependentOnValue: any }) => {
                const vardform = getVardformFromValue(dependentOnValue);
                const isSlutenvard = vardform?.typ === "SLUTENVÅRD";
                return {
                    disabled: !isSlutenvard,
                    required: isSlutenvard,
                    value: isSlutenvard ? value : null,
                };
            },
        },
    ];
</script>

<template>
    <KForm
        ref="formRef"
        id="typoperation"
        class="pad"
        :initial-values-getter="initForm"
        submit-content="Spara och stäng"
        cancel-content="Avbryt"
        @submit="submitForm"
        @cancel="cancelForm"
        :triggers="formTriggers"
        :form-errors="formErrors"
        :errors="inputErrors"
    >
        <template #header>
            <admin-lock-border />
            <h2>{{ heading }}</h2>
        </template>
        <template #default>
            <div class="form-content">
                <div class="form-section">
                    <KText name="namn" label="Namn" validation="required" />

                    <HuvuddiagnoskodAutocomplete />

                    <HuvudoperationskodAutocomplete />
                </div>

                <BidiagnoskodRepeater />

                <BioperationskodRepeater name="opkoder" />

                <div class="form-section">
                    <KText name="beskrivning" label="Beskrivning" type="textarea" class="span-two" />

                    <VardformAutocomplete />

                    <PrioritetAutocomplete />

                    <KNumber name="forberedelser" label="Förberedelsetid (min)" :minValue="0" />

                    <KNumber name="optid" label="Operationstid (min)" :minValue="1" />

                    <KNumber name="avveckling" label="Avvecklingstid (min)" :minValue="0" />

                    <PatientlageAutocomplete name="oplage" label="Patientläge" />

                    <SidaAutocomplete name="lateralitet" />

                    <AnestesiAutocomplete />

                    <BlockadAutocomplete :disabled="true" />

                    <KontaktorsakAutocomplete />

                    <TrombosprofylaxAutocomplete />

                    <AntibiotikaprofylaxAutocomplete />

                    <KNumber
                        name="antal_dagar_pa_avdelning"
                        label="Totalt antal dygn på avdelningen"
                        class="span-one"
                        :minValue="0"
                        :disabled="true"
                    />

                    <KText name="operationsinformation" label="Info till operation" type="textarea" class="span-two" />
                </div>

                <ImplantatRepeater class="span-three" />

                <ResursRepeater class="span-three" />

                <HjalpmedelRepeater class="span-three" />

                <StudieKvalitetsregisterRepeater class="span-three" />

                <FormulateErrors />
            </div>
        </template>
    </KForm>
</template>

<style lang="scss" scoped></style>
